import React, { useState, useEffect } from 'react';
import { Card, Spin, message, Tabs, Row, Col, Button, Space, Modal } from 'antd';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../utils/auth';
import { PlusOutlined } from '@ant-design/icons';
import { SOP_CONSTANTS } from '../../constants/sopConstants';
import { generateUniqueStepCode } from '../../utils/sopUtils';
import sopService from '../../services/sopService';
import dayjs from 'dayjs';
import groupService from '../../services/groupService';

// 引入自定义Hook
import { useTemplateForm } from './hooks/useTemplateForm';
import { useStepManagement } from './hooks/useStepManagement';
import { useFileUpload } from './hooks/useFileUpload';
import { useMessageList } from './hooks/useMessageList';

// 引入子组件
import TemplateHeader from './components/TemplateHeader';
import StepModal from '../../components/sop/StepModal';
import tagService from '../../services/tagService';
import TemplateForm from '../../components/sop/TemplateForm';
import StepList from '../../components/sop/StepList';
import SopMemberExecutions from '../../components/SopMemberExecutions';
import { useFixedFooter, useFixedFooterPlaceholder } from '../../hooks/useFixedFooter';

const SopTemplateEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useAuth();
  const [tags, setTags] = useState([]);
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [hasExecutions, setHasExecutions] = useState(false);
  const [previewUrlsState, setPreviewUrlsState] = useState({});
  const [activeTabKey, setActiveTabKey] = useState('sopInfo');
  const [activating, setActivating] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);

  // 固定底部栏样式
  const fixedFooterStyle = useFixedFooter();
  const placeholderStyle = useFixedFooterPlaceholder();

  // 先定义steps的state
  const [steps, setSteps] = useState([]);

  // 使用自定义Hook
  const {
    template, setTemplate, loading, setLoading, editForm,
    fetchTemplateDetail, isTemplateActive
  } = useTemplateForm(id, tenant, setHasExecutions, setSteps, setPreviewUrlsState);

  // 先初始化messageList相关state
  const [messageList, setMessageList] = useState([]);

  const {
    currentStep, setCurrentStep,
    isStepModalVisible, setIsStepModalVisible, stepForm,
    handleSaveStep, handleAddStep, handleEditStep,
    handleCopyStep, handleDeleteStep
  } = useStepManagement(steps, setSteps, messageList, setMessageList, setPreviewUrlsState);

  const {
    fileList, previewUrls, uploading, uploadProgress,
    handleFileUpload, handleRemoveFile
  } = useFileUpload(stepForm);

  const {
    handleAddMessage, handleDeleteMessage
  } = useMessageList(stepForm, currentStep, setCurrentStep);

  // 返回上一页
  const handleGoBack = () => {
    navigate('/sop-templates');
  };

  // 判断模板是否为激活状态或有执行实例
  const isTemplateDisabled = () => {
    return isTemplateActive();
  };

  // 获取标签列表
  const fetchTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      setTags(tags);
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
    }
  };

  // 获取客户分组列表
  const fetchGroups = async () => {
    try {
      // 设置较大的分页参数，确保获取足够多的群组数据
      const params = {
        page: 0,
        size: 500  // 获取更多的群组数据
      };
      const groupsData = await groupService.getAllGroups(params);

      // 确保每个分组对象都有 id 和 name 属性
      const formattedGroups = Array.isArray(groupsData)
        ? groupsData.map(group => ({
          id: group.id,
          name: group.name || group.groupName || '未命名群组',
          description: group.description,
          memberCount: group.memberCount,
          status: group.status
        }))
        : [];

      setGroups(formattedGroups);
    } catch (error) {
      console.error('获取客户分组列表失败:', error);
      message.error('获取客户分组列表失败');
    }
  };

  // 保存模板
  const handleSaveTemplate = async () => {
    try {
      // 仅检查模板是否处于激活状态
      if (isTemplateActive()) {
        message.error('当前SOP模板处于激活状态，不能修改，请先将其禁用');
        return;
      }

      // 验证表单
      await editForm.validateFields();
      // 获取表单数据
      const formData = editForm.getFieldsValue();
      // 从localStorage获取租户信息
      const tenantStr = localStorage.getItem('tenant');
      if (!tenantStr) {
        message.error('未获取到租户信息，请重新登录');
        return;
      }
      const tenant = JSON.parse(tenantStr);
      const tenantId = tenant.id;

      // 构建请求参数
      const targetType = formData.targetType || SOP_CONSTANTS.TargetType.CUSTOMER;
      // 根据目标类型自动设置触发类型
      let triggerType;
      if (targetType === SOP_CONSTANTS.TargetType.GROUP) {
        triggerType = SOP_CONSTANTS.TriggerType.MANUAL;
      } else {
        // 客户类型：检查是否有触发规则
        const hasTriggerRules = formData.triggerRules && formData.triggerRules.length > 0;
        triggerType = hasTriggerRules ? SOP_CONSTANTS.TriggerType.TAG_CHANGE : SOP_CONSTANTS.TriggerType.MANUAL;
      }
      setSaving(true);
      try {
        // 步骤校验规则
        for (let i = 0; i < steps.length; i++) {
          const step = steps[i];
          if (step.type === 'MESSAGE') {
            const msgList = step.messageList || [];
            // 文字只能有一条
            if (msgList.filter(msg => msg.type === 'TEXT').length > 1) {
              message.error(`第${i + 1}步只能有一条文字消息`);
              setSaving(false);
              return;
            }
            // 附件总数不能超过9个
            const attachmentList = msgList.filter(msg => msg.type !== 'TEXT');
            if (attachmentList.length > 9) {
              message.error(`第${i + 1}步附件总数不能超过9个，当前有${attachmentList.length}个附件`);
              setSaving(false);
              return;
            }
          } else if (step.type === 'MOMENTS') {
            // 朋友圈校验规则
            const momentsList = step.momentsList || [];

            // 文本最多一条
            const textItems = momentsList.filter(item => item.type === 'MOMENT_TEXT');
            if (textItems.length > 1) {
              message.error(`第${i + 1}步朋友圈只能有一条文本内容`);
              setSaving(false);
              return;
            }

            // 图片最多9张（包括图片组中的图片）
            let totalImageCount = 0;
            // 统计单个图片
            const imageItems = momentsList.filter(item => item.type === 'MOMENT_IMAGE');
            totalImageCount += imageItems.length;
            // 统计图片组中的图片
            const imageGroupItems = momentsList.filter(item => item.type === 'MOMENT_IMAGE_GROUP');
            imageGroupItems.forEach(groupItem => {
              if (groupItem.params && groupItem.params.imageGroup && groupItem.params.imageGroup.mediaIds) {
                totalImageCount += groupItem.params.imageGroup.mediaIds.length;
              }
            });
            if (totalImageCount > 9) {
              message.error(`第${i + 1}步朋友圈最多只能上传9张图片，当前有${totalImageCount}张图片`);
              setSaving(false);
              return;
            }

            // 必须有至少一项内容
            if (momentsList.length === 0) {
              message.error(`第${i + 1}步朋友圈必须添加至少一条内容`);
              setSaving(false);
              return;
            }
          }
        }

        // 准备请求数据
        const requestData = {
          template: {
            // 更新时需要ID
            ...(id && id !== 'new' ? { id: parseInt(id) } : {}),
            name: formData.name,
            description: formData.description,
            targetType: formData.targetType,
            triggerType: triggerType,
            triggerRules: formData.triggerRules || [],
            code: formData.code,
            // 确保包含状态字段
            status: template?.status || SOP_CONSTANTS.TemplateStatus.DRAFT,
            // 确保包含租户ID
            tenantId: tenantId,
            // 添加employeeIds字段，支持指定企业微信成员
            employeeIds: formData.employeeIds || [],
            // 添加targetIds字段，用于GROUP类型的SOP存储选择的群组ID，确保转换为数字数组
            targetIds: (formData.targetIds || []).map(id => parseInt(id)).filter(id => !isNaN(id))
          },
          steps: steps.map((step, index) => {
            // 准备要发送的数据
            let actionParams = {};

            // 保存步骤原始类型和动作类型，防止在处理过程中丢失
            const originalType = step.type;
            const originalActionType = step.actionType;

            // 获取原始actionParams数据，如果存在的话
            let originalStepActionParams = {};
            if (step.actionParams) {
              try {
                // 如果是字符串，尝试解析
                if (typeof step.actionParams === 'string') {
                  originalStepActionParams = JSON.parse(step.actionParams);
                }
                // 如果是对象，直接使用
                else if (typeof step.actionParams === 'object') {
                  originalStepActionParams = step.actionParams;
                }
              } catch (e) {
                console.error('解析步骤actionParams失败:', e);
              }
            }

            // 检查步骤是否有原始数据标记(表示这是从后端加载的未编辑步骤)
            const isUnmodifiedStep = step.id && !step._edited;

            // 对于未编辑的步骤，尝试保留完整的原始数据结构
            if (isUnmodifiedStep && typeof step.originalActionParams === 'string') {
              try {
                // 确定后端ActionType
                let backendActionType = step.actionType;
                if (step.type === 'MESSAGE') {
                  backendActionType = 'SEND_MESSAGE';
                } else if (step.type === 'MOMENTS') {
                  backendActionType = 'SEND_MOMENT';
                } else if (step.type === 'TAG') {
                  backendActionType = 'UPDATE_TAG';
                }

                // 使用原始actionParams字符串，避免重新构建可能导致的数据丢失
                return {
                  id: step.id,
                  templateId: parseInt(id),
                  name: step.name,
                  type: step.type,
                  stepCode: step.stepCode,
                  timeOffsetDays: step.timeOffsetDays || 0,
                  executeTime: step.executeTime || null,
                  actionType: backendActionType, // 使用正确的后端actionType
                  actionParams: step.originalActionParams || JSON.stringify(originalStepActionParams),
                  stepOrder: index + 1,
                  description: step.description || '',
                  conditionExpression: step.conditionExpression || null
                };
              } catch (e) {
                console.warn('使用原始步骤数据失败，将进行正常处理:', e);
                // 失败后继续正常处理流程
              }
            }

            // 以下是正常的步骤处理逻辑
            // 根据step.type构建不同的参数
            if (step.type === 'MESSAGE') {
              // 处理消息类型
              actionParams = {
                subtype: 'MESSAGE',
                text: "",
                attachments: []
              };

              // 如果有原始数据且没有messageList，使用原始数据初始化
              if (Object.keys(originalStepActionParams).length > 0 &&
                (!step.messageList || step.messageList.length === 0)) {
                console.log('消息步骤使用原始actionParams数据:', originalStepActionParams);

                // 保留文本内容
                if (originalStepActionParams.text) {
                  actionParams.text = originalStepActionParams.text;
                }

                // 保留附件内容
                if (originalStepActionParams.attachments && Array.isArray(originalStepActionParams.attachments)) {
                  actionParams.attachments = [...originalStepActionParams.attachments];
                }
              }
              // 从messageList中提取消息内容
              else if (step.messageList && step.messageList.length > 0) {
                console.log('保存模板 - 处理messageList:', step.name, step.messageList);
                
                let textMessage = null;
                step.messageList.forEach(msg => {
                  console.log('处理消息项:', msg.type, msg.params);
                  
                  if (msg.type === 'TEXT') {
                    // 设置文本内容
                    textMessage = msg.params.content;
                  } else if (msg.type === 'IMAGE') {
                    // 获取mediaId和ossUrl
                    const mediaId = msg.params.mediaId;
                    const ossUrl = msg.params.ossUrl || '';
                    
                    console.log('处理IMAGE消息:', { mediaId, ossUrl });

                    // 添加到messageList - 修复：使用media_id字段以保持一致性
                    actionParams.attachments.push({
                      msgtype: 'image',
                      image: {
                        media_id: mediaId, // 使用media_id而不是pic_url
                        oss_url: ossUrl // 添加ossUrl
                      }
                    });

                    // 如果ossUrl存在，更新预览URL
                    if (ossUrl && mediaId) {
                      setPreviewUrlsState(prev => ({
                        ...prev,
                        [mediaId]: ossUrl
                      }));
                    }
                  } else if (msg.type === 'IMAGE_GROUP') {
                    // 处理图片组 - 将每张图片作为单独的附件添加
                    const imageGroup = msg.params.imageGroup;
                    console.log('处理IMAGE_GROUP消息:', imageGroup);
                    
                    if (imageGroup && imageGroup.mediaIds && imageGroup.mediaIds.length > 0) {
                      imageGroup.mediaIds.forEach(mediaId => {
                        const ossUrl = imageGroup.ossUrls[mediaId] || '';
                        console.log('处理图片组中的图片:', { mediaId, ossUrl });
                        
                        actionParams.attachments.push({
                          msgtype: 'image',
                          image: {
                            media_id: mediaId,
                            oss_url: ossUrl
                          }
                        });

                        // 如果ossUrl存在，更新预览URL
                        if (ossUrl && mediaId) {
                          setPreviewUrlsState(prev => ({
                            ...prev,
                            [mediaId]: ossUrl
                          }));
                        }
                      });
                    }
                  } else if (msg.type === 'VIDEO') {
                    // 获取mediaId和ossUrl
                    const mediaId = msg.params.mediaId;
                    const ossUrl = msg.params.ossUrl || '';

                    // 添加到messageList
                    actionParams.attachments.push({
                      msgtype: 'video',
                      video: {
                        media_id: mediaId,
                        oss_url: ossUrl // 使用从messageList中提取的ossUrl
                      }
                    });

                    // 如果ossUrl存在，更新预览URL
                    if (ossUrl && mediaId) {
                      setPreviewUrlsState(prev => ({
                        ...prev,
                        [mediaId]: ossUrl
                      }));
                    }
                  } else if (msg.type === 'FILE') {
                    // 获取mediaId和ossUrl
                    const mediaId = msg.params.mediaId;
                    const ossUrl = msg.params.ossUrl || '';

                    // 添加到messageList
                    actionParams.attachments.push({
                      msgtype: 'file',
                      file: {
                        media_id: mediaId,
                        oss_url: ossUrl // 添加ossUrl
                      }
                    });

                    // 如果ossUrl存在，更新预览URL
                    if (ossUrl && mediaId) {
                      setPreviewUrlsState(prev => ({
                        ...prev,
                        [mediaId]: ossUrl
                      }));
                    }
                  } else if (msg.type === 'LINK') {
                    // 获取picUrl和ossUrl
                    const picUrl = msg.params.picUrl || '';
                    const ossUrl = msg.params.picUrl_ossUrl || '';

                    // 添加到messageList
                    actionParams.attachments.push({
                      msgtype: 'link',
                      link: {
                        title: msg.params.title || '',
                        url: msg.params.url || '',
                        desc: msg.params.description || '',
                        picurl: picUrl,
                        pic_oss_url: ossUrl // 添加封面图的ossUrl
                      }
                    });

                    // 如果ossUrl存在，更新预览URL
                    if (ossUrl && picUrl) {
                      setPreviewUrlsState(prev => ({
                        ...prev,
                        [picUrl]: ossUrl
                      }));
                    }
                  } else if (msg.type === 'MINIPROGRAM') {
                    // 获取thumbMediaId及其对应的ossUrl
                    const thumbMediaId = msg.params.thumbMediaId || '';
                    const ossUrl = msg.params.thumbMediaId_ossUrl || '';

                    // 添加到messageList
                    actionParams.attachments.push({
                      msgtype: 'miniprogram',
                      miniprogram: {
                        title: msg.params.title || '',
                        appid: msg.params.appid || '',
                        page: msg.params.pagepath || '',
                        pic_media_id: thumbMediaId,
                        pic_oss_url: ossUrl // 添加封面图的ossUrl
                      }
                    });

                    // 如果ossUrl存在，更新预览URL
                    if (ossUrl && thumbMediaId) {
                      setPreviewUrlsState(prev => ({
                        ...prev,
                        [thumbMediaId]: ossUrl
                      }));
                    }
                  }
                });

                // 如果没有从messageList提取到文本，使用步骤本身的内容
                if (textMessage === null && step.actionType === 'TEXT' && step.actionParams && step.actionParams.content) {
                  textMessage = step.actionParams.content;
                }

                // 设置文本内容
                actionParams.text = textMessage || "";

                // 如果没有附件，且步骤类型是媒体类型，从步骤本身添加附件
                if (actionParams.attachments.length === 0) {
                  if (step.actionType === 'IMAGE' && step.actionParams && step.actionParams.mediaId) {
                    actionParams.attachments.push({
                      msgtype: 'image',
                      image: {
                        media_id: step.actionParams.mediaId, // 使用media_id字段
                        oss_url: step.actionParams.mediaId_ossUrl || '' // 添加ossUrl字段
                      }
                    });
                  } else if (step.actionType === 'VIDEO' && step.actionParams && step.actionParams.mediaId) {
                    actionParams.attachments.push({
                      msgtype: 'video',
                      video: {
                        media_id: step.actionParams.mediaId,
                        oss_url: step.actionParams.ossUrl || step.actionParams.videoMediaId_ossUrl || step.actionParams.mediaId_ossUrl || '' // 尝试多个可能的字段名
                      }
                    });
                  } else if (step.actionType === 'FILE' && step.actionParams && step.actionParams.mediaId) {
                    actionParams.attachments.push({
                      msgtype: 'file',
                      file: {
                        media_id: step.actionParams.mediaId,
                        oss_url: step.actionParams.mediaId_ossUrl || '' // 添加ossUrl字段
                      }
                    });
                  } else if (step.actionType === 'LINK' && step.actionParams) {
                    actionParams.attachments.push({
                      msgtype: 'link',
                      link: {
                        title: step.actionParams.title || '',
                        url: step.actionParams.url || '',
                        desc: step.actionParams.description || '',
                        picurl: step.actionParams.picUrl || '',
                        pic_oss_url: step.actionParams.picUrl_ossUrl || '' // 添加缩略图ossUrl字段
                      }
                    });
                  } else if (step.actionType === 'MINIPROGRAM' && step.actionParams) {
                    actionParams.attachments.push({
                      msgtype: 'miniprogram',
                      miniprogram: {
                        title: step.actionParams.title || '',
                        appid: step.actionParams.appid || '',
                        page: step.actionParams.pagepath || '',
                        pic_media_id: step.actionParams.thumbMediaId || '',
                        pic_oss_url: step.actionParams.thumbMediaId_ossUrl || '' // 添加缩略图ossUrl字段
                      }
                    });
                  }
                }
              }

              console.log('MESSAGE步骤处理完成:', step.name, '附件数量:', actionParams.attachments.length, '附件详情:', actionParams.attachments);
            } else if (step.type === 'TAG') {
              // 获取原始actionParams数据，如果存在的话
              let originalActionParams = {};
              if (step.actionParams) {
                try {
                  // 如果是字符串，尝试解析
                  if (typeof step.actionParams === 'string') {
                    originalActionParams = JSON.parse(step.actionParams);
                  }
                  // 如果是对象，直接使用
                  else if (typeof step.actionParams === 'object') {
                    originalActionParams = step.actionParams;
                  }
                } catch (e) {
                  console.error('解析标签步骤actionParams失败:', e);
                }
              }

              // 标签操作 - 首先尝试使用新设置的数据
              if (step.actionParams && step.actionParams.tagId) {
                actionParams = {
                  operation: step.actionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
                  tagId: step.actionParams.tagId
                };
              }
              // 如果没有新设置的数据，回退到原始数据
              else if (originalActionParams && originalActionParams.tagId) {
                actionParams = {
                  operation: originalActionParams.operation || (step.actionType === 'ADD_TAG' ? 'ADD' : 'REMOVE'),
                  tagId: originalActionParams.tagId
                };
              }
              // 兜底逻辑
              else {
                console.warn('标签步骤没有有效的tagId');
                actionParams = {
                  operation: step.actionType === 'ADD_TAG' ? 'ADD' : 'REMOVE',
                  tagId: null
                };
              }
            } else if (step.type === 'MOMENTS') {
              // 朋友圈消息处理 - 使用与消息相同的数据结构
              actionParams = {
                subtype: 'MOMENTS',
                text: { content: "" },
                attachments: []
                // 不再设置 visible_range，使用后端默认处理 - 根据模板标签自动设置可见范围
              };

              // 获取原始actionParams数据，如果存在的话
              let originalActionParams = {};
              if (step.actionParams) {
                try {
                  // 如果是字符串，尝试解析
                  if (typeof step.actionParams === 'string') {
                    originalActionParams = JSON.parse(step.actionParams);
                  }
                  // 如果是对象，直接使用
                  else if (typeof step.actionParams === 'object') {
                    originalActionParams = step.actionParams;
                  }
                } catch (e) {
                  console.error('解析朋友圈步骤actionParams失败:', e);
                }
              }

              // 如果有原始数据且没有momentsList，使用原始数据初始化
              if (Object.keys(originalActionParams).length > 0 &&
                (!step.momentsList || step.momentsList.length === 0)) {
                console.log('使用原始actionParams数据:', originalActionParams);

                // 保留文本内容
                if (originalActionParams.text && originalActionParams.text.content) {
                  actionParams.text.content = originalActionParams.text.content;
                }

                // 保留附件内容
                if (originalActionParams.attachments && Array.isArray(originalActionParams.attachments)) {
                  actionParams.attachments = [...originalActionParams.attachments];
                }
              }
              // 处理朋友圈内容 - 直接从momentsList获取数据
              else if (step.momentsList && step.momentsList.length > 0) {
                console.log('保存模板，处理朋友圈步骤数据，momentsList:', step.momentsList);

                // 处理文本内容
                const textItems = step.momentsList.filter(item => item.type === 'MOMENT_TEXT');
                if (textItems.length > 0 && textItems[0].params && textItems[0].params.content) {
                  actionParams.text.content = textItems[0].params.content;
                }

                // 处理图片内容 - 支持图片组和单个图片
                // 首先处理图片组
                const imageGroupItems = step.momentsList.filter(item => item.type === 'MOMENT_IMAGE_GROUP');
                imageGroupItems.forEach(groupItem => {
                  if (groupItem.params && groupItem.params.imageGroup) {
                    const { mediaIds, ossUrls } = groupItem.params.imageGroup;
                    if (mediaIds && mediaIds.length > 0) {
                      mediaIds.forEach(mediaId => {
                        actionParams.attachments.push({
                          msgtype: 'image',
                          image: {
                            media_id: mediaId, // 朋友圈附件必须使用专用临时素材
                            oss_url: ossUrls[mediaId] || '' // 添加ossUrl字段
                          }
                        });
                      });
                    }
                  }
                });
                
                // 然后处理单个图片（非图片组的图片）
                const imageItems = step.momentsList.filter(item => item.type === 'MOMENT_IMAGE');
                imageItems.forEach(item => {
                  if (item.params && item.params.mediaId) {
                    actionParams.attachments.push({
                      msgtype: 'image',
                      image: {
                        media_id: item.params.mediaId, // 朋友圈附件必须使用专用临时素材
                        oss_url: item.params.ossUrl || '' // 添加ossUrl字段
                      }
                    });
                  }
                });

                // 处理视频内容
                const videoItems = step.momentsList.filter(item => item.type === 'MOMENT_VIDEO');
                videoItems.forEach(item => {
                  if (item.params && item.params.mediaId) {
                    actionParams.attachments.push({
                      msgtype: 'video',
                      video: {
                        media_id: item.params.mediaId,
                        oss_url: item.params.ossUrl || '' // 添加ossUrl字段
                      }
                    });
                  }
                });

                // 处理链接内容
                const linkItems = step.momentsList.filter(item => item.type === 'MOMENT_LINK');
                linkItems.forEach(item => {
                  if (item.params) {
                    actionParams.attachments.push({
                      msgtype: 'link',
                      link: {
                        title: item.params.title || '',
                        url: item.params.url || '',
                        desc: item.params.description || '',
                        picurl: item.params.picUrl || '',
                        pic_oss_url: item.params.picOssUrl || '' // 添加缩略图ossUrl字段
                      }
                    });
                  }
                });
              }
              // 如果以上条件都不满足，尝试从另一个来源获取数据
              else if (originalType === 'MOMENTS' || originalActionType === 'SEND_MOMENT') {
                console.warn('朋友圈步骤没有momentsList和有效的actionParams，尝试使用其他属性保持内容');

                // 如果有text属性且是对象，使用其内容
                if (step.text && typeof step.text === 'object' && step.text.content) {
                  actionParams.text.content = step.text.content;
                }

                // 如果有attachments属性且是数组，使用其内容
                if (step.attachments && Array.isArray(step.attachments)) {
                  actionParams.attachments = [...step.attachments];
                }
              }
            }

            // 确保actionParams是字符串
            const actionParamsStr = JSON.stringify(actionParams);

            // 根据原始步骤类型确定最终的backend actionType，保持类型一致性
            let finalActionType;
            if (originalType === 'MESSAGE') {
              finalActionType = 'SEND_MESSAGE';
            } else if (originalType === 'MOMENTS') {
              finalActionType = 'SEND_MOMENT';
            } else if (originalType === 'TAG') {
              finalActionType = 'UPDATE_TAG';
            } else if (originalActionType === 'SEND_MESSAGE' || originalActionType === 'SEND_MOMENT' || originalActionType === 'UPDATE_TAG') {
              // 如果原始类型不明确，但有标准actionType，则使用它
              finalActionType = originalActionType;
            } else {
              // 兜底逻辑 - 保持与之前相同
              finalActionType = originalType === 'MESSAGE' ? 'SEND_MESSAGE' :
                originalType === 'MOMENTS' ? 'SEND_MOMENT' : 'UPDATE_TAG';
            }

            return {
              // 如果有ID，则包含ID
              ...(step.id ? { id: step.id } : {}),

              // 如果有模板ID且不是新建模式，则包含模板ID
              ...(id && id !== 'new' ? { templateId: parseInt(id) } : {}),

              name: step.name,
              type: step.type,
              stepCode: step.stepCode || generateUniqueStepCode(),
              timeOffsetDays: step.timeOffsetDays || 0,
              executeTime: (() => {
                // 更健壮的executeTime处理函数
                if (!step.executeTime) return null;

                try {
                  // 判断类型并正确处理
                  if (typeof step.executeTime === 'string') {
                    // 如果是字符串格式，确保格式正确
                    // 首先尝试移除可能存在的毫秒部分
                    const simplifiedTime = step.executeTime.split('.')[0];
                    // 检查是否包含冒号(有效的时间格式)
                    return simplifiedTime.includes(':') ? simplifiedTime : null;
                  } else if (dayjs.isDayjs(step.executeTime) && step.executeTime.isValid()) {
                    // dayjs对象
                    return step.executeTime.format('HH:mm:ss');
                  } else {
                    // 其他情况尝试转换为字符串
                    console.warn('未知的executeTime格式:', step.executeTime);
                    return null;
                  }
                } catch (e) {
                  console.error('处理executeTime失败:', e);
                  return null;
                }
              })(),
              // 保留原有的actionType逻辑，但增加额外的安全检查
              actionType: finalActionType,
              actionParams: actionParamsStr,
              stepOrder: index + 1,
              description: step.description || '',
              conditionExpression: step.conditionExpression || null
            };
          })
        };

        // 处理触发规则
        if (formData.triggerRules && formData.triggerRules.length > 0) {
          requestData.template.triggerRules = formData.triggerRules.map(rule => ({
            tagId: rule.tagId,
            operation: rule.operation || SOP_CONSTANTS.Operation.ADD,
            excludeTagIds: rule.excludeTagIds || [], // 确保包含排除标签ID列表
            isActive: true, // 默认激活
            // 如果有ID，则包含ID
            ...(rule.id ? { id: rule.id } : {})
          }));
        }

        if (!id || id === 'new') {
          // 创建模式
          const result = await sopService.createSopWithSteps(requestData, tenantId);
          message.success('创建成功');
          // 如果是新创建的模板，更新URL以反映新ID，但不导航到新页面
          if (result && result.id) {
            navigate(`/sop-templates/${result.id}`, { replace: true });
          }
        } else {
          // 更新模式
          await sopService.updateSopWithSteps(id, requestData, tenantId);
          message.success('保存成功');
        }

        // 重新加载模板数据以更新UI
        fetchTemplateDetail();
        return true; // 返回成功标识
      } catch (error) {
        console.error('保存失败:', error);
        message.error('保存失败: ' + (error.response?.data?.message || error.message));
        return false; // 返回失败标识
      } finally {
        setSaving(false);
      }
    } catch (error) {
      console.error('表单验证失败:', error);
      message.error('请填写必填字段');
      return false; // 返回失败标识
    }
  };

  // 仅保存模板（带弹窗提醒）
  const handleSaveOnly = async () => {
    console.log('handleSaveOnly called, isTemplateActive:', isTemplateActive());
    const success = await handleSaveTemplate();
    console.log('Save result:', success);
    
    // 如果保存成功且模板不是激活状态，弹窗询问是否激活
    if (success === true && !isTemplateActive()) {
      console.log('Showing activate modal');
      setShowActivateModal(true);
    }
  };

  // 保存并激活
  const handleSaveAndActivate = async () => {
    try {
      // 先保存模板
      await handleSaveTemplate();
      
      // 如果保存成功，直接激活
      await handleActivateTemplate();
    } catch (error) {
      console.error('保存并激活失败:', error);
    }
  };

  // 处理激活确认
  const handleActivateConfirm = async () => {
    setShowActivateModal(false);
    await handleActivateTemplate();
  };

  // 激活模板
  const handleActivateTemplate = async () => {
    if (!id || id === 'new') return;
    
    try {
      setActivating(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      await sopService.activateTemplate(id, tenantId);
      message.success('推送已激活');
      
      // 重新加载模板数据以更新UI
      fetchTemplateDetail();
    } catch (error) {
      console.error('激活推送失败:', error);
      message.error('激活推送失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setActivating(false);
    }
  };

  // 禁用模板
  const handleDeactivateTemplate = async () => {
    if (!id || id === 'new') return;
    
    try {
      setActivating(true);
      
      // 从本地存储获取租户ID
      const tenantStr = localStorage.getItem('tenant');
      let tenantId;
      
      try {
        if (tenantStr) {
          const tenantObj = JSON.parse(tenantStr);
          tenantId = tenantObj.id;
        } else {
          throw new Error('找不到租户信息');
        }
      } catch (error) {
        console.error('解析tenant信息失败:', error);
        throw new Error('租户信息解析失败，无法继续操作');
      }
      
      await sopService.deactivateTemplate(id, tenantId);
      message.success('推送已禁用');
      
      // 重新加载模板数据以更新UI
      fetchTemplateDetail();
    } catch (error) {
      console.error('禁用推送失败:', error);
      message.error('禁用推送失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setActivating(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    console.log('useEffect 触发, id:', id, 'location.search:', location.search);
    
    fetchTemplateDetail();
    fetchTags();
    fetchGroups();

    // 检查是否需要切换到执行情况标签
    if (location.state && location.state.tab === 'executionStatus' && id && id !== 'new') {
      setActiveTabKey('executionStatus');
    }

    // 处理URL参数 - 从旅程节点创建SOP时自动填充标签信息
    const isCreateMode = !id || id === 'create';
    console.log('检查条件: isCreateMode:', isCreateMode, 'id:', id, 'location.search存在:', !!location.search, 'pathname:', location.pathname);
    
    if (isCreateMode && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const tagId = searchParams.get('tagId');
      const tagName = searchParams.get('tagName');
      const nodeName = searchParams.get('nodeName');
      
      console.log('URL参数解析结果:', { tagId, tagName, nodeName });
      
      if (tagId && tagName) {
        console.log('检测到完整的标签URL参数:', { tagId, tagName });
        
        // 使用递归检查表单是否已经初始化，然后设置值
        const trySetFormValues = (attempts = 0) => {
          console.log(`尝试设置表单值，第${attempts + 1}次, editForm:`, !!editForm);
          
          if (editForm && editForm.getFieldsValue) {
            try {
              console.log('开始设置表单值...');
              
              // 设置基本字段和触发规则
              editForm.setFieldsValue({
                name: `${decodeURIComponent(tagName)}_智能体`,
                targetType: 'CUSTOMER',
                triggerRules: [
                  {
                    tagId: parseInt(tagId),
                    operation: 'ADD'
                  }
                ]
              });
              
              console.log('表单值设置完成:', editForm.getFieldsValue());
            } catch (error) {
              console.error('设置表单值失败:', error);
              if (attempts < 10) {
                setTimeout(() => trySetFormValues(attempts + 1), 500);
              }
            }
          } else {
            console.log('editForm 未初始化，重试...');
            if (attempts < 10) {
              setTimeout(() => trySetFormValues(attempts + 1), 500);
            }
          }
        };
        
        trySetFormValues();
      } else if (nodeName) {
        console.log('只检测到nodeName参数，设置基本信息:', { nodeName });
        
        // 只有节点名称，设置基本信息但不设置触发规则
        const trySetFormValues = (attempts = 0) => {
          console.log(`尝试设置表单值(仅nodeName)，第${attempts + 1}次, editForm:`, !!editForm);
          
          if (editForm && editForm.getFieldsValue) {
            try {
              console.log('开始设置表单值(仅nodeName)...');
              
              // 只设置名称和目标类型
              editForm.setFieldsValue({
                name: `${decodeURIComponent(nodeName)}_智能体`,
                targetType: 'CUSTOMER'
              });
              
              console.log('表单值设置完成(仅nodeName):', editForm.getFieldsValue());
            } catch (error) {
              console.error('设置表单值失败:', error);
              if (attempts < 10) {
                setTimeout(() => trySetFormValues(attempts + 1), 500);
              }
            }
          } else {
            console.log('editForm 未初始化，重试...');
            if (attempts < 10) {
              setTimeout(() => trySetFormValues(attempts + 1), 500);
            }
          }
        };
        
        trySetFormValues();
      } else {
        console.log('tagId、tagName 和 nodeName 都缺失');
      }
    } else {
      console.log('条件不满足，跳过自动填充');
    }
  }, [id, location.state, location.search, editForm]);

  return (
    <div className="sop-template-edit">
      <TemplateHeader
        id={id}
        handleGoBack={handleGoBack}
        handleSaveTemplate={handleSaveOnly}
        saving={saving}
        isTemplateActive={isTemplateActive}
        handleActivateTemplate={handleActivateTemplate}
        handleDeactivateTemplate={handleDeactivateTemplate}
        activating={activating}
      />

      <Spin spinning={loading} tip="加载中...">
        <div className="template-content">
          <Tabs activeKey={activeTabKey} onChange={setActiveTabKey} style={{ marginBottom: 16 }}>
            <Tabs.TabPane tab="信息" key="sopInfo">
              <Card title="基本信息" style={{ marginBottom: 24 }}>
                <TemplateForm
                  form={editForm}
                  tags={tags}
                  groups={groups}
                  onTargetTypeChange={() => { }}
                  isTemplateActive={isTemplateDisabled()}
                />
              </Card>

              <Card
                title="内容序列"
                style={{ marginBottom: 24 }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAddStep}
                  disabled={isTemplateDisabled()}
                  title={isTemplateDisabled() ? "模板已激活或有执行实例，不允许添加步骤" : ""}
                  style={{ marginBottom: 16 }}
                >添加内容</Button>
                <StepList
                  steps={steps}
                  onEditStep={handleEditStep}
                  onCopyStep={handleCopyStep}
                  onDeleteStep={handleDeleteStep}
                  isTemplateActive={isTemplateDisabled()}
                  preserveOriginal={true}
                />
                
                {/* 在StepList下方添加一个添加内容按钮 */}
                {steps.length > 0 && (
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleAddStep}
                    disabled={isTemplateDisabled()}
                    title={isTemplateDisabled() ? "模板已激活或有执行实例，不允许添加步骤" : ""}
                    style={{ 
                      marginTop: 16, 
                      width: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}
                  >添加内容</Button>
                )}
              </Card>

            </Tabs.TabPane>

            {id && id !== 'new' && (
              <Tabs.TabPane tab="执行情况" key="executionStatus">
                <Card>
                  <SopMemberExecutions
                    templateId={id}
                    title="推送执行情况"
                  />
                </Card>
              </Tabs.TabPane>
            )}
          </Tabs>
        </div>
      </Spin>

      {/* 为固定按钮栏预留空间 */}
      <div style={placeholderStyle}></div>

      {/* 固定底部按钮栏 */}
      <div style={fixedFooterStyle}>
        <Space>
          <Button size="middle" onClick={handleGoBack}>
            取消
          </Button>
          <Button
            type="primary"
            onClick={handleSaveOnly}
            loading={saving}
            disabled={isTemplateDisabled()}
            size="middle"
          >
            仅保存
          </Button>
          <Button
            type="primary"
            onClick={handleSaveAndActivate}
            loading={saving || activating}
            disabled={isTemplateDisabled()}
            size="middle"
          >
            保存并激活
          </Button>
        </Space>
      </div>

      <StepModal
        open={isStepModalVisible}
        title={currentStep ? '编辑内容' : '添加内容'}
        currentStep={currentStep}
        form={stepForm}
        messageList={messageList}
        previewUrls={previewUrlsState}
        uploading={uploading}
        uploadProgress={uploadProgress}
        onSave={handleSaveStep}
        onCancel={() => {
          setIsStepModalVisible(false);
          setCurrentStep(null);
          setMessageList([]);
          stepForm.resetFields();
        }}
        onAddMessage={handleAddMessage}
        onDeleteMessage={handleDeleteMessage}
        handleFileUpload={handleFileUpload}
        handleRemoveFile={handleRemoveFile}
        isTemplateActive={isTemplateDisabled()}
        tags={tags}
      />

      {/* 激活确认弹窗 */}
      <Modal
        title="激活智能体"
        open={showActivateModal}
        onOk={handleActivateConfirm}
        onCancel={() => setShowActivateModal(false)}
        okText="立即激活"
        cancelText="稍后激活"
        confirmLoading={activating}
      >
        <p>SOP已保存成功！是否要立即激活此智能体？</p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          激活后，智能体将开始按照设置的规则自动执行。
        </p>
      </Modal>
    </div>
  );
};

export default SopTemplateEdit;